import { useState, type ReactNode } from 'react';

import { Avatar } from '../components/Avatar.js';
import { AvatarStack } from '../components/AvatarStack.js';
import { Icon } from '../components/Icon.js';
import type { InboxItem } from '../fixtures/domain.js';
import styles from './ScopeBox.module.css';

/** Faces in the box before the rest collapse into the "+N" that opens the list. */
const SCOPE_FACES = 4;

interface ScopeBoxProps {
  /** The conversations the surrounding dialog is about, in list order. */
  targets: InboxItem[];
  /** A strip along the bottom of the box (where the list picker lives). */
  foot?: ReactNode;
  /** Rendered over the box — e.g. a progress frost while a run is in flight. */
  overlay?: ReactNode;
}

/**
 * Who a dialog is about: every face it reaches, and every name behind the "+N".
 * One box, one height, whatever is inside it — so swapping faces for names or
 * for the picker in its foot moves nothing and the buttons stay put.
 */
export function ScopeBox({ targets, foot, overlay }: ScopeBoxProps) {
  const [listing, setListing] = useState(false);

  return (
    <div className={listing ? styles.boxList : styles.box}>
      <div className={listing ? styles.contentList : styles.content}>
        {listing ? (
          <div className={styles.people}>
            {targets.map((item) => (
              <span key={item.channel.id} className={styles.person}>
                <Avatar
                  name={item.counterpart.name}
                  src={item.counterpart.avatarUrl}
                  locked={item.counterpart.locked}
                  size={28}
                />
                <span className={styles.personText}>
                  <span className={styles.personName}>{item.counterpart.name}</span>
                  {item.channel.jobMeta ? (
                    <span className={styles.jobTag}>
                      <Icon name="briefcase" size={11} filled={false} />
                      <span className={styles.jobTagText}>{item.channel.jobMeta.jobTitle}</span>
                    </span>
                  ) : (
                    <span className={styles.personJob}>Direct message</span>
                  )}
                </span>
              </span>
            ))}
          </div>
        ) : (
          <AvatarStack
            people={targets.map((item) => item.counterpart)}
            max={SCOPE_FACES}
            size={44}
            onMore={() => setListing(true)}
          />
        )}
      </div>
      {foot && <div className={styles.foot}>{foot}</div>}
      {overlay}
    </div>
  );
}
